import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axios';

export const getProviderGroups = createAsyncThunk('getProviderGroups', async (providers) => {
  const response = await axiosInstance.get('provider-groups/', { params: { providers } });
  return response.data;
});

export const getProviderGroup = createAsyncThunk('getProviderGroup', async (id) => {
  const response = await axiosInstance.get(`provider-groups/${id}`);
  return response.data;
});

export const createProviderGroup = createAsyncThunk(
  'createProviderGroup',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      dispatch(getProviderGroups());
      const response = await axiosInstance.post('provider-groups/', data);
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const updateProviderGroup = createAsyncThunk(
  'updateProviderGroup',
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`provider-groups/${id}/`, data);
      dispatch(getProviderGroups());
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const deleteProviderGroup = createAsyncThunk(
  'deleteProviderGroup',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.delete(`provider-groups/${id}`);
      dispatch(getProviderGroups());
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const deleteManyProviderGroup = createAsyncThunk(
  'deleteManyProviderGroup',
  async (groups, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.delete(`provider-groups/delete_many_groups/`, {
        params: {
          groups,
        },
      });
      dispatch(getProviderGroups());
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const searchUser = createAsyncThunk('searchUser', async (params, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`user/search/`, { params });
    return response.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const addUserToGroup = createAsyncThunk('addUserToGroup', async (data, { dispatch, rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('provider-groups/add_user_to_groups/', data);
    return response.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const addChargerGroupToProviderGroup = createAsyncThunk(
  'addUserToGroup',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('provider-groups/add_charger_groups_to_user_groups/', data);
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const removeChargerGroupFromProviderGroup = createAsyncThunk(
  'addUserToGroup',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete('provider-groups/remove_charger_groups_from_user_groups/', { data });
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const removeUserFromGroup = createAsyncThunk(
  'removeUserFromGroup',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete('provider-groups/remove_user_from_groups/', { data });
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

const initialState = {
  providerGroups: [],
  providerGroup: null,
  user: null,
  users: [],
  userId: null,
  loading: false,
  searching: false,
  isSubmitting: false,
  showChargerGroupsDialog: false,
};

const slice = createSlice({
  name: 'ProviderGroups',
  initialState,
  reducers: {
    resetUser: (state) => {
      state.user = null;
    },

    resetUserId: (state, action) => {
      if (state.user && state.user.id === action.payload) {
        state.user = null;
      }
    },
    setShowChargerGroupsDialog: (state, action) => {
      state.showChargerGroupsDialog = action.payload;
    },
    resetProviderGroup(state) {
      state.providerGroup = null;
    },
    resetProviderGroups(state) {
      state.providerGroups = [];
    },
  },
  extraReducers: {
    [getProviderGroups.pending]: (state) => {
      state.loading = true;
    },
    [getProviderGroups.fulfilled]: (state, action) => {
      const { data } = action.payload;
      state.providerGroups = data;
      state.loading = false;
    },
    [getProviderGroups.rejected]: (state) => {
      state.loading = false;
    },
    // ---------------------------------------------
    [getProviderGroup.pending]: (state, action) => {
      state.loading = true;
    },
    [getProviderGroup.fulfilled]: (state, action) => {
      const { data } = action.payload;
      state.providerGroup = data;
      state.loading = false;
    },
    [getProviderGroup.rejected]: (state) => {
      state.loading = false;
    },
    // ---------------------------------------------
    [createProviderGroup.pending]: (state) => {
      state.isSubmitting = true;
    },
    [createProviderGroup.fulfilled]: (state) => {
      state.isSubmitting = false;
    },
    [createProviderGroup.rejected]: (state) => {
      state.isSubmitting = false;
    },
    // ---------------------------------------------
    [deleteProviderGroup.pending]: (state) => {
      state.isSubmitting = true;
    },
    [deleteProviderGroup.fulfilled]: (state) => {
      state.isSubmitting = false;
    },
    [deleteProviderGroup.rejected]: (state) => {
      state.isSubmitting = false;
    },
    // ---------------------------------------------
    [updateProviderGroup.pending]: (state) => {
      state.isSubmitting = true;
    },
    [updateProviderGroup.fulfilled]: (state) => {
      state.isSubmitting = false;
    },
    [updateProviderGroup.rejected]: (state) => {
      state.isSubmitting = false;
    },
    // ---------------------------------------------
    [searchUser.pending]: (state) => {
      state.searching = true;
    },
    [searchUser.fulfilled]: (state, action) => {
      const { data } = action.payload;
      //state.user = data;
      state.users = data;
      state.searching = false;
    },
    [searchUser.rejected]: (state) => {
      state.user = null;
      state.searching = false;
    },
    // ---------------------------------------------
    [addUserToGroup.pending]: (state) => {
      state.isSubmitting = true;
    },
    [addUserToGroup.fulfilled]: (state) => {
      const userIdToRemove = action.payload.user_id;
      state.users = state.users.filter((user) => user.id !== userIdToRemove);
      state.isSubmitting = false;
    },
    [addUserToGroup.rejected]: (state) => {
      state.isSubmitting = false;
    },
    // ---------------------------------------------
    [removeUserFromGroup.pending]: (state) => {
      state.isSubmitting = true;
    },
    [removeUserFromGroup.fulfilled]: (state) => {
      state.isSubmitting = false;
    },
    [removeUserFromGroup.rejected]: (state) => {
      state.isSubmitting = false;
    },
    // ---------------------------------------------
    [addChargerGroupToProviderGroup.pending]: (state) => {
      state.isSubmitting = true;
    },
    [addChargerGroupToProviderGroup.fulfilled]: (state) => {
      state.isSubmitting = false;
    },
    [addChargerGroupToProviderGroup.rejected]: (state) => {
      state.isSubmitting = false;
    },
    // ---------------------------------------------
    [removeChargerGroupFromProviderGroup.pending]: (state) => {
      state.isSubmitting = true;
    },
    [removeChargerGroupFromProviderGroup.fulfilled]: (state) => {
      state.isSubmitting = false;
    },
    [removeChargerGroupFromProviderGroup.rejected]: (state) => {
      state.isSubmitting = false;
    },
    // ---------------------------------------------
    [deleteManyProviderGroup.pending]: (state) => {
      state.isSubmitting = true;
    },
    [deleteManyProviderGroup.fulfilled]: (state) => {
      state.isSubmitting = false;
    },
    [deleteManyProviderGroup.rejected]: (state) => {
      state.isSubmitting = false;
    },
    // ---------------------------------------------
  },
});

export const { resetUser, setShowChargerGroupsDialog, resetProviderGroup, resetProviderGroups, resetUserId } =
  slice.actions;

export default slice.reducer;
